/** English strings of the logistics v2 features (`logOpt.*`) — typed against the Polish ones (a missing key is a compile error). */
import { logOptPl, type Shape } from './logistics-optimization-pl';

export const logOptEn: Shape<typeof logOptPl> = {
  view: 'Optimization',
  subtitle: 'Where the fleet loses kilometres and money — route scores, suggestions and a what-if simulator.',
  loadError: 'Could not load the route analysis',
  common: {
    estimate: 'Estimate (straight line)',
    estimateHint: 'The routing server is unavailable — km are straight-line × 1.25.',
    roads: 'By road',
    skipped: 'Without coordinates: {count}',
    skippedHint: 'Deliveries to customers without coordinates are not on the map.',
    truncated: 'Too many points — some were left out',
    depot: 'Depot',
    fit: 'Show all',
    perMonth: '{value}/mo',
    noCostAccess: 'Costs are visible to company-wide scope only.',
    simulate: 'Simulate',
    minutes: '{value} min',
    hours: '{h} h {m} min',
  },
  map: {
    aria: 'Map of the runs by road',
    legend: 'Runs of the day',
    all: 'All',
    showRoute: 'Show route {code}',
    stops: 'Stops: {count}',
    leg: 'Leg {km} · {min}',
    seq: 'Stop {seq}',
    loadingRoads: 'Routing by road…',
  },
  run: {
    title: 'Run of the day',
    subtitle: 'Optimal order by road: depot → stops → depot',
    noRun: 'The route did not run on this day',
    noRunBody: 'Pick another day from the run list or use the arrows.',
    loadError: 'Could not route the run',
    distance: 'Distance',
    duration: 'Driving time',
    empty: 'Empty running',
    emptyHint: '{pct} of the run without goods for a customer',
    recorded: 'Recorded km',
    optimal: 'Optimal km',
    excess: '+{km} over the optimal loop',
    onPlan: 'Within the optimal loop',
    noRecorded: 'No km in the costs of this day',
    timeline: 'Stop order',
    timelineHint: 'Leg and time from the previous point',
    depotStart: 'Leaves the depot',
    depotEnd: 'Back at the depot',
    emptyLeg: 'empty running',
    total: 'Total',
    openDelivery: 'Open delivery: {customer}',
    stopsList: 'Deliveries of the run',
  },
  detail: {
    efficiency: 'Route efficiency',
    efficiencyHint: 'Last {days} days against the fleet',
    noEfficiency: 'No efficiency data for the route in the period.',
    suggestions: 'Suggestions for the route',
    noSuggestions: 'No suggestions — the route runs efficiently.',
    costs: 'Costs, 30 days',
    costsHint: 'Daily route cost split by component',
    simulateRoute: 'Simulate route',
    toOptimization: 'Optimization',
  },
  metric: {
    score: 'Score',
    fill: 'Load',
    empty: 'Empty running',
    excess: 'Excess km',
    kmPerDelivery: 'Km / delivery',
    costPerDelivery: 'Cost / delivery',
    costShare: 'Cost % of revenue',
    runsPerWeek: 'Runs / week',
    avgStops: 'Stops / run',
    revenuePerKm: 'Revenue / km',
  },
  hero: {
    saving: 'Savings potential',
    savingHint: 'from {count} suggestions',
    savingKm: 'Km to win back',
    savingKmHint: 'per month',
    empty: 'Fleet empty running',
    emptyHint: 'km depot ↔ customers',
    excess: 'Excess km',
    excessHint: 'over the optimal loop',
    fill: 'Van load',
    fillHint: 'capacity: {capacity} stops',
    costPerDelivery: 'Cost / delivery',
    costPerDeliveryHint: '{runs} runs, {deliveries} deliveries',
    insightTop: 'Biggest opportunity: {title} — {saving}.',
    insightNone: 'Routes run efficiently — no suggestions in this period.',
  },
  period: { label: 'Analysis period', d30: '30 days', d90: '90 days', range: '{from} – {to}' },
  section: {
    suggestions: 'Suggestions',
    suggestionsHint: 'Biggest saving first. “Simulate” recalculates the change without saving.',
    ranking: 'Route ranking',
    rankingHint:
      'Routes with the most room for improvement first. Score 0–100: load, empty running and cost per delivery against the fleet.',
    weekdays: 'Weekday load',
    weekdaysHint: 'Average van load on the day of the run',
    compare: 'Route loop map',
    compareHint: 'Typical loop through the route’s customers by road from the depot',
  },
  severity: { HIGH: 'High priority', MEDIUM: 'Medium priority', LOW: 'Low priority' },
  suggestion: {
    saving: 'Saving',
    savingUnknown: 'saving to be estimated',
    openRoute: 'Route {code}',
    openCustomer: 'Customer card',
    all: 'All',
    empty: 'No suggestions',
    emptyBody: 'Routes run efficiently in this period. Try a longer period or the simulator.',
    list: 'Suggestion list',
    filter: 'Suggestion type filter',
  },
  kind: {
    REORDER: {
      label: 'Order',
      title: 'Better stop order',
      text: 'Route {route} drives {recordedKm} per run on average, while the optimal loop by road is {optimalKm} — {excessKm} ({excessPct}) too much.',
      fallback:
        'Route {route} drives further than the optimal loop by road. Put the stops in the optimal order.',
    },
    REDUCE_FREQUENCY: {
      label: 'Frequency',
      title: 'Fewer runs',
      text: 'Route {route} runs {runsPerWeek}× a week ({weekdays}) at {fillPct} load. Running only on {newWeekdays} raises the load to {newFillPct}.',
      fallback: 'Route {route} runs with low van load. Fewer runs would carry the same deliveries.',
    },
    REASSIGN_CUSTOMER: {
      label: 'Assignment',
      title: 'Customer on another route',
      text: '{customer} is {nearestOwnKm} from the nearest customer of route {route}, but only {nearestTargetKm} from route {target}. Moving it saves {kmSavedPerVisit} per delivery on {route} and adds {kmAddedPerVisit} to {target}.',
      fallback: '{customer} is closer to route {target} than to route {route}. Consider moving the customer.',
    },
    EMPTY_RUNS: {
      label: 'Empty running',
      title: 'High empty running',
      text: 'On route {route}, {emptyPct} of the kilometres are getting out of the depot and back: {emptyKm} per run, {emptyKmMonthly} a month. Consider a carrier, a cross-dock or fewer runs.',
      fallback:
        'On route {route} a large share of the kilometres is driving out of the depot and back without goods.',
    },
    UNPROFITABLE_DELIVERIES: {
      label: 'Profitability',
      title: 'Unprofitable deliveries',
      text: 'A delivery to {customer} costs {costPerDelivery} on average, while its margin is {marginPerDelivery} — a loss of {lossMonthly} a month. Consider a minimum order or fewer deliveries.',
      fallback:
        'Deliveries to {customer} cost more than the margin of its orders. Consider a minimum order or fewer deliveries.',
    },
    WEAK_WEEKDAY: {
      label: 'Weekday',
      title: 'Weak run day',
      text: 'On {weekday} the vans run at {fillPct} load, on the other days at {otherFillPct}. That day’s deliveries fit into other runs.',
      fallback: 'One weekday has clearly lower van load. Move its deliveries to other days.',
    },
  },
  ranking: {
    sort: 'Route sorting',
    by: { score: 'Score', fill: 'Load', empty: 'Empty running', cost: 'Cost / delivery' },
    runsPerWeek: '{value}× / week',
    deliveries: 'Deliveries: {count}',
    weekdays: 'Run days',
    showMap: 'Show the loop on the map',
    open: 'Route details',
    empty: 'No runs in the period',
    scoreAria: 'Efficiency score: {score} of 100',
  },
  band: { good: 'Efficient', ok: 'To improve', bad: 'Weak' },
  weekday: { d1: 'Monday', d2: 'Tuesday', d3: 'Wednesday', d4: 'Thursday', d5: 'Friday' },
  weekdayIn: { d1: 'Mondays', d2: 'Tuesdays', d3: 'Wednesdays', d4: 'Thursdays', d5: 'Fridays' },
  wd: { d1: 'Mon', d2: 'Tue', d3: 'Wed', d4: 'Thu', d5: 'Fri' },
  chart: {
    fill: 'Load',
    runs: 'Runs',
    deliveries: 'Deliveries',
    avgStops: 'Stops / run',
    threshold: 'Low load threshold',
    empty: 'No runs in the period',
    costsAria: 'Chart of the route’s daily costs',
    weekdaysAria: 'Chart of van load by weekday',
  },
  compare: {
    route: 'Route on the map',
    customers: 'Customers: {count}',
    loop: 'Loop {km} · {time}',
    empty: 'No route customers with coordinates',
    loadError: 'Could not route the loop',
    deliveries: 'Deliveries: {count}',
    revenue: 'Revenue: {value}',
  },
  territory: {
    roads: 'Loop by road · {km}',
    estimate: 'Estimate (straight line) · {km}',
    fallback: 'Indicative line (nearest neighbour)',
    truncated: 'The loop shows the key customers',
    hint: 'The line shows the typical loop through the route’s customers by road, from the depot and back (period: {days} days).',
  },
  sim: {
    open: 'Simulator',
    title: 'What-if simulator',
    subtitle: 'Combine changes and compare runs, kilometres and costs before and after.',
    notice: 'Simulation — nothing was changed in the route plan or the data.',
    changes: 'Changes',
    add: 'Add a change',
    remove: 'Remove change',
    empty: 'Add at least one change to calculate a scenario.',
    run: 'Calculate scenario',
    reset: 'Clear',
    incomplete: 'Fill in the fields of every change.',
    error: 'Could not calculate the simulation',
    days: 'Baseline period',
    kind: {
      MOVE_CUSTOMER: 'Move a customer',
      SET_RUN_DAYS: 'Route run days',
      OPTIMIZE_ORDER: 'Optimal order',
      STOP_CAPACITY: 'Van capacity',
    },
    kindHint: {
      MOVE_CUSTOMER: 'The customer and its deliveries move to another route.',
      SET_RUN_DAYS: 'Which days the route runs — deliveries of the other days merge into them.',
      OPTIMIZE_ORDER: 'The route’s runs follow the optimal order by road.',
      STOP_CAPACITY: 'How many stops one van takes on this route.',
    },
    fromRoute: 'From route',
    toRoute: 'To route',
    route: 'Route',
    pickRoute: 'Pick a route',
    customer: 'Customer',
    customerSearch: 'Search the route’s customers…',
    noCustomers: 'No matching customers',
    pickRouteFirst: 'Pick the source route first',
    weekdays: 'Run days',
    capacity: 'Stops per van',
    result: 'Simulation result',
    before: 'Before',
    after: 'After',
    perRoute: 'Result per route',
    assumptions: 'Model assumptions',
    warnings: 'Warnings',
    noCost: 'Costs unavailable — visible to company-wide scope only.',
    better: 'better',
    worse: 'worse',
    metric: {
      runsPerMonth: 'Runs / mo',
      vansPerMonth: 'Van-days / mo',
      kmPerMonth: 'Km / mo',
      costPerMonth: 'Cost / mo',
      avgFillPct: 'Load',
      avgStopsPerRun: 'Stops / run',
      deliveriesPerMonth: 'Deliveries / mo',
    },
  },
  assumption: {
    generic: '{key}: {value}',
    stopCapacity: 'Van capacity: {value} stops',
    workingDaysPerMonth: 'Working days per month: {value}',
    periodDays: 'Baseline period: {value} days',
    sampleRuns: 'OSRM sample runs per route: {value}',
    kmModel: 'Run km: depot legs + a part growing with the number of stops (√n)',
    kmSource: 'Distances: {value}',
    fixedCostPerVanDay: 'Fixed cost per van-day (driver + vehicle): {value}',
    costPerKm: 'Cost per km: {value}',
    costPerStop: 'Cost per stop: {value}',
  },
  source: {
    osrm: 'by road (OSRM)',
    straight: 'straight line × 1.25',
    mixed: 'by road and estimated',
    none: 'no data',
  },
  warning: {
    generic: 'Note: {key}',
    noRuns: 'The route has no runs in the period — the change has nothing to build on.',
    fewerVisits: 'On the target route the customer would get fewer deliveries a week than now.',
    noKm: 'No route kilometres — the result is an estimate.',
    noRecordedKm: 'No recorded km in the costs — no comparison with the optimal loop.',
    overCapacity: 'Van capacity exceeded — an extra van is needed.',
    noDeliveries: 'The customer has no deliveries in the period — change skipped.',
    sameRoute: 'The customer already rides on this route — change skipped.',
    noCoords: 'Customer without coordinates — its kilometres were not counted.',
  },
};
